import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { X, Users, Mountain } from 'lucide-react';
import { getPresignedUploadUrl, commitPhoto, confirmShares, skipReview } from '../api/photos';
import { updateLocation } from '../api/location';
import { saveLocationToHistory } from '../services/attributeMatcher';
import { collectProximityAttributes, storeProximitySnapshot } from '../services/proximityService';
import ReviewShareModal from '../components/ReviewShareModal';

function Camera({ user }) {
  const navigate = useNavigate();
  const [cameraActive, setCameraActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // Face scan toggle — "People" mode (default) vs "Scenery" mode
  const [skipFaceScan, setSkipFaceScan] = useState(false);

  // Smart Share state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [commitResult, setCommitResult] = useState(null);

  // Upload progress tracking
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState(''); // 'capturing', 'getting-url', 'uploading', 'processing'

  useEffect(() => {
    startCamera();
    getCurrentLocation();

    const locationInterval = setInterval(() => {
      getCurrentLocation();
    }, 120000);

    return () => {
      clearInterval(locationInterval);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  // Camera error state for fallback UI
  const [cameraError, setCameraError] = useState(null);

  // When the stream is obtained, attach it to the video element
  // This runs after render so videoRef.current is guaranteed to exist
  useEffect(() => {
    if (cameraActive && streamRef.current && videoRef.current && !videoRef.current.srcObject) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch((err) => {
      });
    }
  }, [cameraActive]);

  const startCamera = async () => {
    setCameraError(null);

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      const msg = 'Camera API not available. Make sure you are using HTTPS and a supported browser.';
      setCameraError(msg);
      toast.error(msg);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });

      streamRef.current = stream;
      setCameraActive(true);
    } catch (error) {
      let msg = 'Camera access denied. Please enable camera permissions.';
      if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        msg = 'No camera found on this device.';
      } else if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        msg = 'Camera permission denied. Please allow camera access in your browser settings.';
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        msg = 'Camera is in use by another application. Close other apps using the camera and try again.';
      } else if (error.name === 'OverconstrainedError') {
        // Fall back to any available camera
        try {
          const fallbackStream = await navigator.mediaDevices.getUserMedia({ video: true });
          streamRef.current = fallbackStream;
          setCameraActive(true);
          return;
        } catch (_) {
          msg = 'Could not access any camera on this device.';
        }
      }
      setCameraError(msg);
      toast.error(msg);
    }
  };

  const getCurrentLocation = () => {
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const loc = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        setCurrentLocation(loc);
        updateLocation(loc.latitude, loc.longitude);
        saveLocationToHistory(loc.latitude, loc.longitude);
        
        // Store proximity snapshot for verification
        await storeProximitySnapshot(loc.latitude, loc.longitude);
      },
      () => {
        toast.error('Location access denied. Enable location for auto-sharing.');
      }
    );
  };

  // Haptic feedback utility
  const triggerHapticFeedback = (pattern = 'success') => {
    if ('vibrate' in navigator) {
      switch (pattern) {
        case 'success':
          // Short double vibration for success
          navigator.vibrate([50, 30, 50]);
          break;
        case 'capture':
          // Single short pulse for capture
          navigator.vibrate(30);
          break;
        case 'error':
          // Longer vibration for error
          navigator.vibrate(200);
          break;
        default:
          navigator.vibrate(50);
      }
    }
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !currentLocation) {
      toast.error('Please enable location to capture photos');
      return;
    }

    if (videoRef.current.readyState !== videoRef.current.HAVE_ENOUGH_DATA) {
      toast.error('Camera not ready. Please wait a moment.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadStage('capturing');
    
    // Haptic feedback on capture
    triggerHapticFeedback('capture');

    try {
      // Stage 1: Capture from video (5%)
      setUploadProgress(5);
      const canvas = document.createElement('canvas');
      const width = videoRef.current.videoWidth;
      const height = videoRef.current.videoHeight;

      if (width === 0 || height === 0) {
        throw new Error('Invalid video dimensions');
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0, width, height);

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
      if (!blob) throw new Error('Failed to create image blob');

      const filename = `photo_${Date.now()}.jpg`;
      setUploadProgress(10);

      // Stage 2: Get presigned URL (15%)
      setUploadStage('getting-url');
      const { upload_url, s3_key } = await getPresignedUploadUrl(filename, 'image/jpeg');
      setUploadProgress(15);

      // Stage 3: Upload to S3 with real progress tracking (15% - 80%)
      setUploadStage('uploading');
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            // Map upload progress from 15% to 80%
            const uploadPercent = (event.loaded / event.total) * 65;
            setUploadProgress(Math.round(15 + uploadPercent));
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`S3 upload failed: ${xhr.status} ${xhr.statusText}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error during upload'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload cancelled'));
        });

        xhr.open('PUT', upload_url);
        xhr.setRequestHeader('Content-Type', 'image/jpeg');
        xhr.send(blob);
      });

      setUploadProgress(80);

      // Stage 4: Process photo with face recognition (80% - 100%)
      setUploadStage('processing');

      // Collect full proximity attributes (GPS + WiFi + Bluetooth)
      const attributes = await collectProximityAttributes(
        currentLocation.latitude,
        currentLocation.longitude
      );

      setUploadProgress(85);

      const result = await commitPhoto(
        s3_key,
        filename,
        currentLocation.latitude,
        currentLocation.longitude,
        attributes,
        skipFaceScan
      );

      setUploadProgress(100);
      
      // Haptic feedback on success
      triggerHapticFeedback('success');

      if (skipFaceScan) {
        // No face scan — save as private photo
        toast.success('Photo saved! (Face scan skipped)');
        navigate('/feed');
      } else if (result.matched_faces && result.matched_faces.length > 0 && result.needs_review) {
        setCommitResult(result);
        setShowReviewModal(true);
      } else if (result.matched_faces && result.matched_faces.length > 0 && !result.needs_review) {
        // All faces were auto-shared via cache
        toast.success(
          `Photo auto-shared with ${result.auto_shared_count} ${result.auto_shared_count === 1 ? 'person' : 'people'}!`
        );
        navigate('/feed');
      } else {
        // No matches found
        toast.success('Photo saved! No recognized faces detected.');
        navigate('/feed');
      }
    } catch (error) {
      // Haptic feedback on error
      triggerHapticFeedback('error');
      toast.error(error.message || 'Failed to upload photo');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadStage('');
    }
  };

  // Smart Share handlers
  const handleConfirmShares = async (recipientIds) => {
    if (!commitResult) return;
    try {
      const result = await confirmShares(commitResult.id, recipientIds);
      toast.success(
        `Shared with ${result.shares_created} ${result.shares_created === 1 ? 'person' : 'people'}!`
      );
      setShowReviewModal(false);
      navigate('/feed');
    } catch (error) {
      toast.error(error.message || 'Failed to share photo');
    }
  };

  const handleSkipReview = async () => {
    if (!commitResult) return;
    try {
      const result = await skipReview(commitResult.id);
      toast.success(
        `Shared with ${result.shares_created} ${result.shares_created === 1 ? 'person' : 'people'}!`
      );
      setShowReviewModal(false);
      navigate('/feed');
    } catch (error) {
      toast.error(error.message || 'Failed to share photo');
    }
  };

  const handleKeepPrivate = () => {
    toast.success('Photo saved privately.');
    setShowReviewModal(false);
    navigate('/feed');
  };

  return (
    <div className="camera-preview">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: cameraActive ? 'block' : 'none',
        }}
      />

      {/* Camera Error Fallback */}
      {cameraError && !cameraActive && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            textAlign: 'center',
            color: 'white',
            zIndex: 15,
          }}
          data-testid="camera-error"
        >
          <X size={48} color="#ef4444" style={{ marginBottom: '1rem' }} />
          <p style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem' }}>{cameraError}</p>
          <button
            onClick={startCamera}
            data-testid="camera-retry-button"
            style={{
              marginTop: '1rem',
              padding: '0.75rem 1.5rem',
              borderRadius: '9999px',
              border: 'none',
              background: 'hsl(188, 82%, 35%)',
              color: 'white',
              fontSize: '0.9rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Retry Camera
          </button>
        </div>
      )}

      {/* Top Bar */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          padding: '1rem',
          background: 'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, transparent 100%)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 10,
          pointerEvents: 'all',
        }}
      >
        <button
          onClick={() => navigate('/feed')}
          data-testid="camera-close-button"
          style={{
            background: 'rgba(255,255,255,0.2)',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            backdropFilter: 'blur(10px)',
          }}
        >
          <X size={24} color="white" />
        </button>

        {currentLocation && (
          <span
            data-testid="location-indicator"
            style={{
              background: 'rgba(22, 163, 74, 0.9)',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '9999px',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            Location: ON
          </span>
        )}
      </div>

      {/* Photo mode toggle — People vs Scenery */}
      {!uploading && !showReviewModal && (
        <div
          style={{
            position: 'absolute',
            top: '4.5rem',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            background: 'rgba(0, 0, 0, 0.45)',
            backdropFilter: 'blur(12px)',
            borderRadius: '9999px',
            padding: '0.25rem',
            zIndex: 10,
          }}
          data-testid="photo-mode-toggle"
        >
          <button
            onClick={() => setSkipFaceScan(false)}
            data-testid="mode-people"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: '0.5rem 1rem',
              borderRadius: '9999px',
              border: 'none',
              background: !skipFaceScan ? 'hsl(188, 82%, 35%)' : 'transparent',
              color: 'white',
              fontSize: '0.8rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background-color 200ms ease',
            }}
          >
            <Users size={16} />
            People
          </button>
          <button
            onClick={() => setSkipFaceScan(true)}
            data-testid="mode-scenery"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: '0.5rem 1rem',
              borderRadius: '9999px',
              border: 'none',
              background: skipFaceScan ? 'hsl(188, 82%, 35%)' : 'transparent',
              color: 'white',
              fontSize: '0.8rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background-color 200ms ease',
            }}
          >
            <Mountain size={16} />
            Scenery
          </button>
        </div>
      )}

      {/* Shutter Button */}
      {!uploading && !showReviewModal && (
        <button
          className="shutter-button"
          onClick={capturePhoto}
          disabled={!currentLocation}
          data-testid="capture-button"
        />
      )}

      {uploading && (
        <div
          style={{
            position: 'absolute',
            bottom: '6rem',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(255,255,255,0.95)',
            padding: '1.25rem 2rem',
            borderRadius: '1rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.75rem',
            zIndex: 20,
            minWidth: '200px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          }}
          data-testid="upload-progress-container"
        >
          {/* Progress bar */}
          <div style={{
            width: '100%',
            height: '6px',
            background: 'hsl(var(--muted))',
            borderRadius: '3px',
            overflow: 'hidden',
          }}>
            <div
              style={{
                width: `${uploadProgress}%`,
                height: '100%',
                background: 'linear-gradient(90deg, hsl(188, 82%, 35%) 0%, hsl(188, 82%, 45%) 100%)',
                borderRadius: '3px',
                transition: 'width 0.3s ease',
              }}
              data-testid="upload-progress-bar"
            />
          </div>
          
          {/* Percentage and stage */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            width: '100%',
          }}>
            <span style={{ 
              fontSize: '0.85rem', 
              color: 'hsl(var(--muted-foreground))',
              fontWeight: 500,
            }}>
              {uploadStage === 'capturing' && 'Capturing...'}
              {uploadStage === 'getting-url' && 'Preparing...'}
              {uploadStage === 'uploading' && 'Uploading...'}
              {uploadStage === 'processing' && 'Processing...'}
            </span>
            <span style={{ 
              fontSize: '0.9rem', 
              fontWeight: 600,
              color: 'hsl(var(--primary))',
            }}>
              {uploadProgress}%
            </span>
          </div>
        </div>
      )}

      {/* Smart Share Review Modal */}
      <ReviewShareModal
        open={showReviewModal}
        photoUrl={commitResult?.s3_url}
        matchedFaces={commitResult?.matched_faces || []}
        onConfirm={handleConfirmShares}
        onSkip={handleSkipReview}
        onCancel={handleKeepPrivate}
      />
    </div>
  );
}

export default Camera;
